import React, { useState } from 'react';
import { Alert, Button, Snackbar } from '@mui/material';
import { ChatBubbleOutline as MessageIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useChat } from '../../contexts/ChatContext';

/**
 * Opens a direct conversation with another member of the organization. Hidden when signed out,
 * when the current user isn't a member of this organization, or on the user's own profile.
 */
const MessageMemberButton = ({ userTenantId, firstName, ...buttonProps }) => {
  const { isAuthenticated } = useAuth();
  const { userTenantId: currentUserTenantId, openDirectChat } = useChat();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const targetId = Number(userTenantId);
  if (!isAuthenticated || !currentUserTenantId || !targetId || targetId === currentUserTenantId) return null;

  const open = async () => {
    setBusy(true);
    try {
      const channel = await openDirectChat(targetId);
      if (channel) navigate(`/chat/${channel.id}?compose=1`);
      else setError(`${firstName || 'This member'} can't be messaged.`);
    } catch (err) {
      setError(err.message || 'Failed to open the conversation.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<MessageIcon />}
        onClick={open}
        disabled={busy}
        aria-label={firstName ? `Message ${firstName}` : 'Message'}
        {...buttonProps}
      >
        Message
      </Button>
      <Snackbar open={Boolean(error)} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)} sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>
    </>
  );
};

export default MessageMemberButton;
